import type {
  Favorite, IndexPreferences, ActionIntent, AuthSession, BillingUsage, Bootstrap, Conversation, ConversationSummary,
  CrmRecord, DailyBrief, GoogleStart, ModelSelection, RecordContext, RecordDetailResponse,
  PageContext, Personalization, RecordListResponse, RecordQuery, SchemaDescriptor, SearchResult,
  StagedImport, StreamEvent,
  ThemePreference, TimelineEvent, TokenPair,
} from './api-types';
import { activePersonalAnthropicApiKey, removePersonalAnthropicApiKey } from './personal-api-key';
import { clearSession, loadSession, saveSession } from './session';

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A single in-flight renewal shared by every caller, so a burst of 401s
 * produces one refresh rather than a thundering herd that burns the
 * rotating refresh token.
 */
let renewal: Promise<string | null> | null = null;

async function renewAccessToken(): Promise<string | null> {
  const session = loadSession();
  if (!session) return null;
  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/renew`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: session.tokens.refreshToken.token }),
    });
    if (!response.ok) {
      clearSession();
      return null;
    }
    const renewed = (await response.json()) as { tokens: TokenPair; bootstrap: Bootstrap };
    saveSession({ tokens: renewed.tokens, bootstrap: renewed.bootstrap });
    return renewed.tokens.accessToken.token;
  } catch {
    return null;
  }
}

function ensureRenewal(): Promise<string | null> {
  renewal ??= renewAccessToken().finally(() => {
    renewal = null;
  });
  return renewal;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set for the auth routes, which must not carry (or retry with) a token. */
  anonymous?: boolean;
  signal?: AbortSignal;
  personalKey?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.personalKey) { const key = activePersonalAnthropicApiKey(); if (key) headers['X-Resonance-Anthropic-API-Key'] = key; }
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  };

  const initialToken = options.anonymous ? null : (loadSession()?.tokens.accessToken.token ?? null);
  let response = await send(initialToken);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await ensureRenewal();
    if (refreshed) response = await send(refreshed);
  }

  if (!response.ok) throw await toApiError(response);
  if (options.method && options.method !== 'GET' && /^\/v1\/(email|connections|connection-operations|incoming-connections|incoming-requests)/.test(path)) window.dispatchEvent(new Event('navigation-status-changed'));
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = 'request_failed';
  let message = response.status >= 500
    ? 'The Resonance server is unavailable.'
    : `The request failed (${response.status}).`;
  let details: unknown;
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
    details = body.error?.details;
  } catch {
    /* Non-JSON error bodies keep the generic message. */
  }
  return new ApiError(response.status, code, message, details);
}

export interface AiProviderStatus { saved: boolean; enabled: boolean; suffix?: string }

// Migrate a browser-only key on an explicit background-feature activation.
// Do not enable background billing merely by loading a page.
export async function saveBrowserKeyForBackground(): Promise<void> {
  const key = activePersonalAnthropicApiKey();
  if (!key) return;
  await request<AiProviderStatus>('/v1/settings/ai-provider', { method: 'PUT', body: { key } });
  removePersonalAnthropicApiKey();
}

export const api = {
  /* ---------- Auth ---------- */
  googleStart: (callback: string) =>
    request<GoogleStart>(`/v1/auth/google/start?callback=${encodeURIComponent(callback)}`, {
      anonymous: true,
    }),

  exchange: (code: string, appearance?: 'system' | 'dark' | 'light') =>
    request<AuthSession>('/v1/auth/exchange', {
      method: 'POST', body: { code, appearance }, anonymous: true,
    }),

  password: (input: {
    email: string; password: string; displayName?: string; workspaceName?: string;
    appearance?: 'system' | 'dark' | 'light';
  }) => request<AuthSession>('/v1/auth/password', { method: 'POST', body: input, anonymous: true }),

  /* ---------- Workspace ---------- */
  bootstrap: () => request<Bootstrap>('/v1/mobile/bootstrap?emailWorkspace=web'),
  schema: () => request<SchemaDescriptor>('/v1/schema'),
  updateIndexPreferences: (object: string, preferences: IndexPreferences) =>
    request<{ object: string; preferences: IndexPreferences }>(`/v1/index-preferences/${encodeURIComponent(object)}`, { method: 'PATCH', body: preferences }),
  getTheme: () => request<ThemePreference>('/v1/settings/theme'),
  updateTheme: (theme: ThemePreference) =>
    request<ThemePreference>('/v1/settings/theme', { method: 'PUT', body: theme }),
  personalization: () => request<Personalization>('/v1/settings/personalization'),
  updatePersonalization: (input: { personalization?: string; goals?: string }) =>
    request<Personalization>('/v1/settings/personalization', { method: 'PUT', body: input }),
  aiProvider: () => request<AiProviderStatus>('/v1/settings/ai-provider'),
  saveAiProviderKey: (key: string) => request<AiProviderStatus>('/v1/settings/ai-provider', { method: 'PUT', body: { key } }),
  setAiProviderEnabled: (enabled: boolean) => request<AiProviderStatus>('/v1/settings/ai-provider', { method: 'PATCH', body: { enabled } }),
  removeAiProviderKey: () => request<AiProviderStatus>('/v1/settings/ai-provider', { method: 'DELETE' }),
  billingUsage: () => request<BillingUsage>('/v1/settings/billing-usage'),

  /* ---------- Records ---------- */
  listRecords: (object: string, params: { limit?: number; offset?: number; sortField?: string; sortDirection?: 'asc' | 'desc' } = {}) => {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    if (params.sortField) query.set('sortField', params.sortField);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);
    const suffix = query.size ? `?${query}` : '';
    return request<RecordListResponse>(`/v1/records/${encodeURIComponent(object)}${suffix}`);
  },

  listFavorites: () => request<{ favorites: Favorite[] }>('/v1/favorites'),
  addFavorite: (recordId: string) => request<{ favorite: Pick<Favorite, 'id' | 'recordId' | 'position'> }>('/v1/favorites', {
    method: 'POST', body: { recordId },
  }),
  removeFavorite: (recordId: string) => request<{ success: boolean }>(`/v1/favorites/${encodeURIComponent(recordId)}`, { method: 'DELETE' }),

  getRecord: (object: string, id: string) =>
    request<RecordDetailResponse>(
      `/v1/records/${encodeURIComponent(object)}/${encodeURIComponent(id)}`,
    ),

  queryRecords: (object: string, query: RecordQuery) =>
    request<{ records: CrmRecord[]; [key: string]: unknown }>(
      `/v1/records/${encodeURIComponent(object)}/query`,
      { method: 'POST', body: query },
    ),

  createRecord: (object: string, data: Record<string, unknown>) =>
    request<{ record: CrmRecord }>(`/v1/records/${encodeURIComponent(object)}`, {
      method: 'POST',
      body: { data },
    }),

  updateRecord: (object: string, id: string, data: Record<string, unknown>) =>
    request<{ record: CrmRecord }>(
      `/v1/records/${encodeURIComponent(object)}/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: { data } },
    ),

  deleteRecord: (object: string, id: string) =>
    request<{ success: boolean }>(
      `/v1/records/${encodeURIComponent(object)}/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  /* ---------- Search, timeline, context ---------- */
  search: (query: string, options: { object?: string; limit?: number } = {}) => {
    const params = new URLSearchParams({ q: query });
    if (options.object) params.set('object', options.object);
    if (options.limit) params.set('limit', String(options.limit));
    return request<{ results: SearchResult[] }>(`/v1/search?${params}`);
  },

  timeline: (options: { record?: string; actorKind?: string; since?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (options.record) params.set('record', options.record);
    if (options.actorKind) params.set('actorKind', options.actorKind);
    if (options.since) params.set('since', options.since);
    if (options.limit) params.set('limit', String(options.limit));
    const suffix = params.size ? `?${params}` : '';
    return request<{ events: TimelineEvent[] }>(`/v1/timeline${suffix}`);
  },

  recordContext: (id: string) =>
    request<RecordContext>(`/v1/context/record/${encodeURIComponent(id)}`),

  /* ---------- Chat ---------- */
  listConversations: () => request<{ conversations: ConversationSummary[] }>('/v1/conversations'),
  createConversation: () => request<{ conversation: Conversation }>('/v1/conversations', { method: 'POST' }),
  lastConversation: () => request<{ conversation: Conversation | null }>('/v1/conversations/last'),
  getConversation: (id: string) =>
    request<{ conversation: Conversation }>(`/v1/conversations/${encodeURIComponent(id)}`),
  deleteConversation: (id: string) =>
    request<{ success: boolean }>(`/v1/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  cancelTurn: (conversationId: string, turnId: string) =>
    request<{ accepted: boolean; turn_id: string }>(
      `/v1/conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/cancel`,
      { method: 'POST' },
    ),
  stageImport: (conversationId: string, input: { filename: string; mediaType: string; dataBase64: string }) =>
    request<StagedImport>(`/v1/conversations/${encodeURIComponent(conversationId)}/imports`, {
      method: 'POST',
      body: { filename: input.filename, media_type: input.mediaType, data_base64: input.dataBase64 },
    }),

  /* ---------- Proactive ---------- */
  dailyBrief: () => request<DailyBrief>('/v1/daily-brief'),
  setDailyBriefEnabled: async (enabled: boolean) => {
    if (enabled) await saveBrowserKeyForBackground();
    return request<{ enabled: boolean }>('/v1/daily-brief/settings', { method: 'PUT', body: { enabled } });
  },
  markBriefSeen: () => request<{ success: boolean }>('/v1/daily-brief/seen', { method: 'POST' }),
  actionIntents: () => request<{ actionIntents: ActionIntent[] }>('/v1/action-intents'),
  decideActionIntent: (id: string, decision: 'approve' | 'reject', editedPayload?: Record<string, unknown>) =>
    request<{ success: boolean }>(`/v1/action-intents/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: { decision, ...(editedPayload ? { edited_payload: editedPayload } : {}) },
    }),
};

/**
 * Streams an assistant reply.
 *
 * The server streams over POST, so `EventSource` (GET-only) cannot be used —
 * this parses the SSE framing off the fetch body stream directly.
 */
export async function streamMessage(
  conversationId: string,
  input: { text: string; model: ModelSelection; turnId: string; pageContext?: PageContext; signal?: AbortSignal },
  onEvent: (event: StreamEvent) => void,
): Promise<void> {
  const path = `/v1/conversations/${encodeURIComponent(conversationId)}/messages/stream`;
  await streamSse(path, {
    text: input.text,
    model_selection: input.model,
    turn_id: input.turnId,
    ...(input.pageContext ? { page_context: input.pageContext } : {}),
  }, onEvent, input.signal);
}

export async function streamAutoProgress(
  conversationId: string,
  input: { model: ModelSelection; turnId: string; pageContext?: PageContext; signal?: AbortSignal },
  onEvent: (event: StreamEvent) => void,
): Promise<void> {
  const path = `/v1/conversations/${encodeURIComponent(conversationId)}/auto-progress/stream`;
  await streamSse(path, { model_selection: input.model, turn_id: input.turnId, ...(input.pageContext ? { page_context: input.pageContext } : {}) }, onEvent, input.signal);
}

async function streamSse(
  path: string,
  body: unknown,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const personalApiKey = activePersonalAnthropicApiKey();
  const open = async (token: string | null): Promise<Response> =>
    fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(personalApiKey ? { 'x-resonance-anthropic-api-key': personalApiKey } : {}),
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

  let response = await open(loadSession()?.tokens.accessToken.token ?? null);
  if (response.status === 401) {
    const refreshed = await ensureRenewal();
    if (refreshed) response = await open(refreshed);
  }
  if (!response.ok) throw await toApiError(response);
  if (!response.body) throw new ApiError(500, 'no_stream', 'The server returned no stream.');

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;

      // SSE frames are separated by a blank line; a partial trailing frame
      // stays in the buffer until the rest of it arrives.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(frame);
        if (parsed) onEvent(parsed);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string): StreamEvent | null {
  let name = '';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) name = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!name || dataLines.length === 0) return null;
  try {
    return { event: name, data: JSON.parse(dataLines.join('\n')) } as StreamEvent;
  } catch {
    return null;
  }
}
